import { h, render } from 'preact';
import { Provider } from 'react-redux';

import { store } from '../store';
import { Modal } from './components/modal/modal.component';

interface ModalConfig {
  component: any;
  props?: any;
  onClose?: () => void;
  ariaLabel?: string;
  maxWidth?: number;
  borderRadius?: string;
  borderWidth?: string;
  borderColor?: string;
}

class ModalService {
  private container: HTMLElement | null = null;
  private activeModals: Set<string> = new Set();
  private stack: string[] = [];
  private components: Map<string, ModalConfig['component']> = new Map();

  private ensureContainer() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'modal-root';
      document.body.appendChild(this.container);
    }
    return this.container;
  }

  /** True while a modal rendering `component` is open and not yet closing. */
  isOpen(component: ModalConfig['component']): boolean {
    return this.findOpen(component) !== undefined;
  }

  private findOpen(component: ModalConfig['component']): string | undefined {
    return this.stack.find((id) => this.components.get(id) === component);
  }

  open(config: ModalConfig): string {
    // One modal per component. Callers that open after an async step (the
    // need-access request, the auth checks) can be re-entered by a second tap
    // before the first modal is up; the repeat is a no-op (SNGL-7251).
    const existing = this.findOpen(config.component);
    if (existing) {
      return existing;
    }

    const modalId = `modal-${Date.now()}-${Math.random()}`;
    const container = this.ensureContainer();

    const close = () => {
      this.close(modalId);
      config.onClose?.();
    };

    const modalWrapper = document.createElement('div');
    modalWrapper.id = modalId;
    container.appendChild(modalWrapper);

    render(
      // @ts-ignore
      h(
        Provider,
        { store },
        h(Modal, {
          onClose: close,
          isTopmost: () => this.stack[this.stack.length - 1] === modalId,
          ariaLabel: config.ariaLabel,
          maxWidth: config.maxWidth,
          borderRadius: config.borderRadius,
          borderWidth: config.borderWidth,
          borderColor: config.borderColor,
          children: h(config.component, { ...config.props, close }),
        })
      ),
      modalWrapper
    );

    this.activeModals.add(modalId);
    this.stack.push(modalId);
    this.components.set(modalId, config.component);
    return modalId;
  }

  close(modalId: string) {
    const modalElement = document.getElementById(modalId);
    const modalContent = modalElement?.querySelector('.single-modal-content');
    if (modalElement && modalContent) {
      modalContent.classList.add('closing');

      // Wait for closing animation to finish
      this.stack = this.stack.filter((id) => id !== modalId);
      this.components.delete(modalId);
      setTimeout(() => {
        render(null, modalElement);
        modalElement.remove();
        this.activeModals.delete(modalId);
      }, 150);
    }
  }

  closeAll() {
    this.activeModals.forEach((id) => this.close(id));
  }
}

export const modalService = new ModalService();
